/**
 * useCurrency - Hook for managing user's currency based on profile
 *
 * This hook provides access to the user's currency settings derived from their
 * restaurant profile. It automatically handles fallbacks and provides formatting
 * utilities that work with the user's specific currency.
 */

import { useMemo } from 'react';
import { useAuth } from './useAuth';
import {
  getCurrencyForCountry,
  formatCurrencyForCountry,
  formatCurrencyWithSymbolForCountry,
  DEFAULT_COUNTRY,
  type CurrencyInfo,
} from '@utils/currency';
import {
  getCurrencyForCountryCode,
  getCountryByCode,
} from '@data/countries-cities';

// ============================================================================
// Types
// ============================================================================

export interface UseCurrencyResult {
  /** Current user's currency information */
  currency: CurrencyInfo;
  /** Currency code (e.g., 'EUR', 'USD') */
  currencyCode: string;
  /** Currency symbol (e.g., '€', '$') */
  currencySymbol: string;
  /** Locale for formatting (e.g., 'es-ES', 'en-US') */
  locale: string;
  /** User's country name */
  country: string;
  /** Whether currency data is available */
  isAvailable: boolean;
  /** Format amount using user's currency */
  formatAmount: (amount: number) => string;
  /** Format amount with symbol using user's currency */
  formatAmountWithSymbol: (amount: number) => string;
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to get user's currency information from their profile
 *
 * @returns Currency information and formatting functions
 *
 * @example
 * ```tsx
 * const { currency, formatAmount, currencyCode } = useCurrency();
 *
 * return (
 *   <div>
 *     <p>Currency: {currency.name}</p>
 *     <p>Price: {formatAmount(99.99)}</p>
 *     <p>Code: {currencyCode}</p>
 *   </div>
 * );
 * ```
 */
export function useCurrency(): UseCurrencyResult {
  const { profile, user } = useAuth();

  return useMemo(() => {
    // Get country code from profile (should always be ISO country code now)
    const countryCode = profile?.country || DEFAULT_COUNTRY;
    let currencyCode = profile?.currency; // Use explicit currency if available

    // Derive currency from country code if not explicitly set
    if (!currencyCode) {
      currencyCode = getCurrencyForCountryCode(countryCode);
    }

    // Get country name for display
    const countryData = getCountryByCode(countryCode);
    const country = countryData?.name || 'España';

    // Get currency information
    const currency = getCurrencyForCountry(country);

    // Override with correct currency code if we derived it differently
    if (currencyCode && currencyCode !== currency.code) {
      // Find currency info by code
      const currencies = {
        EUR: {
          code: 'EUR',
          symbol: '€',
          name: 'Euro',
          locale: 'es-ES',
          decimals: 2,
        },
        USD: {
          code: 'USD',
          symbol: '$',
          name: 'US Dollar',
          locale: 'en-US',
          decimals: 2,
        },
        GBP: {
          code: 'GBP',
          symbol: '£',
          name: 'British Pound',
          locale: 'en-GB',
          decimals: 2,
        },
        ARS: {
          code: 'ARS',
          symbol: '$',
          name: 'Argentine Peso',
          locale: 'es-AR',
          decimals: 2,
        },
        CHF: {
          code: 'CHF',
          symbol: 'CHF',
          name: 'Swiss Franc',
          locale: 'de-CH',
          decimals: 2,
        },
        // Add more as needed
      };

      const overrideCurrency =
        currencies[currencyCode as keyof typeof currencies];
      if (overrideCurrency) {
        Object.assign(currency, overrideCurrency);
      }
    }

    // Check if we have valid currency data
    const isAvailable = !!(profile?.country || user);

    // Create formatting functions bound to the user's currency
    const formatAmount = (amount: number): string => {
      return formatCurrencyForCountry(amount, country);
    };

    const formatAmountWithSymbol = (amount: number): string => {
      return formatCurrencyWithSymbolForCountry(amount, country);
    };

    return {
      currency,
      currencyCode: currency.code,
      currencySymbol: currency.symbol,
      locale: currency.locale,
      country,
      isAvailable,
      formatAmount,
      formatAmountWithSymbol,
    };
  }, [profile?.country, profile?.currency, user]);
}

// ============================================================================
// Utility Hooks
// ============================================================================

/**
 * Hook to get just the currency code for the current user
 *
 * @returns Currency code string (e.g., 'EUR')
 *
 * @example
 * ```tsx
 * const currencyCode = useCurrencyCode();
 * // currencyCode = 'EUR'
 * ```
 */
export function useCurrencyCode(): string {
  const { currencyCode } = useCurrency();
  return currencyCode;
}

/**
 * Hook to get just the currency symbol for the current user
 *
 * @returns Currency symbol string (e.g., '€')
 *
 * @example
 * ```tsx
 * const symbol = useCurrencySymbol();
 * // symbol = '€'
 * ```
 */
export function useCurrencySymbol(): string {
  const { currencySymbol } = useCurrency();
  return currencySymbol;
}

/**
 * Hook to get a currency formatter function for the current user
 *
 * @returns Formatting function
 *
 * @example
 * ```tsx
 * const formatCurrency = useCurrencyFormatter();
 * const formattedPrice = formatCurrency(99.99);
 * // formattedPrice = '99,99 €'
 * ```
 */
export function useCurrencyFormatter(): (amount: number) => string {
  const { formatAmount } = useCurrency();
  return formatAmount;
}

/**
 * Hook to check if user has currency information available
 *
 * @returns Boolean indicating if currency data is available
 *
 * @example
 * ```tsx
 * const hasCurrency = useHasCurrency();
 * if (!hasCurrency) {
 *   return <p>Please complete your profile setup</p>;
 * }
 * ```
 */
export function useHasCurrency(): boolean {
  const { isAvailable } = useCurrency();
  return isAvailable;
}

// ============================================================================
// Default Export
// ============================================================================

export default useCurrency;
